import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { useSettings } from '../../context/SettingsContext';
import { mobileApi } from '../../services/api';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import MobileNav from '../../components/mobile/MobileNav';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { QrCode, CheckCircle2, XCircle, Loader2, ChevronRight, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const MobileInspections = () => {
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const { formatDate } = useSettings();

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('due');
  const [due, setDue] = useState([]);
  const [history, setHistory] = useState([]);
  const [histTotal, setHistTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const histOffset = useRef(0);
  const PAGE = 20;

  const load = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (tab === 'due') { const r = await mobileApi.getInspectionsDue(token); setDue(r.data || []); }
      else {
        histOffset.current = 0;
        const r = await mobileApi.getInspectionHistory(token, PAGE, 0);
        setHistory(r.data?.items || []);
        setHistTotal(r.data?.total || 0);
        histOffset.current = (r.data?.items || []).length;
      }
    } catch { toast.error('Failed to load inspections'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const histHasMore = history.length < histTotal;
  const loadMoreHist = useCallback(async () => {
    if (loadingMore || !histHasMore) return;
    setLoadingMore(true);
    try {
      const r = await mobileApi.getInspectionHistory(getToken(), PAGE, histOffset.current);
      setHistory((prev) => [...prev, ...(r.data?.items || [])]);
      histOffset.current += (r.data?.items || []).length;
    } catch { /* noop */ }
    setLoadingMore(false);
  }, [loadingMore, histHasMore, getToken]);
  const histSentinel = useInfiniteScroll(histHasMore, loading || loadingMore, loadMoreHist);

  const overdue = due.filter((d) => d.overdue_days > 0);
  const dueToday = due.filter((d) => !d.overdue_days);

  const Row = ({ item }) => {
    const a = item.asset;
    const od = item.overdue_days;
    const spine = od ? 'var(--overdue)' : 'var(--volt-edge)';
    const model = [a.make, a.model].filter(Boolean).join(' ');
    return (
      <MobileCard spine={spine} className="mb-2.5" padding="normal"
        onClick={() => navigate(`/mobile/inspections/new/${a.id}/${item.template.id}`)}>
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] shrink-0">{a.unit_number || a.name}</span>
              {model && <span className="text-[13px] text-[var(--ink-secondary)] truncate">{model}</span>}
            </div>
            <div className="flex items-center gap-1.5 mt-1.5 text-[13px]">
              {od ? (
                <>
                  <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, background: 'var(--overdue)' }} />
                  <span className="font-semibold text-[var(--overdue-ink)]">Overdue {od} {od === 1 ? 'day' : 'days'}</span>
                </>
              ) : (
                <span className="text-[var(--ink-tertiary)]">Due today</span>
              )}
              <span className="text-[var(--ink-tertiary)]">· {item.template.item_count} items</span>
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/mobile/inspections/new/${a.id}/${item.template.id}`); }}
            data-testid={`insp-start-${a.id}`}
            className="shrink-0 h-11 px-5 rounded-[12px] bg-white text-[var(--ink-primary)] text-[13px] font-bold"
            style={{ border: '1.5px solid var(--border-strong)' }}
          >
            Start
          </button>
        </div>
      </MobileCard>
    );
  };

  const SectionHeader = ({ label, count, tone }) => (
    <div className="flex items-center gap-3 mb-3 mt-1">
      <span className="text-[11px] font-bold uppercase tracking-[0.11em]" style={{ color: tone === 'overdue' ? 'var(--overdue-ink)' : '#6E6A61' }}>{label}</span>
      <span className={`font-mono text-[11px] px-1.5 py-0.5 rounded-[4px] ${tone === 'overdue' ? 'bg-[var(--overdue-bg)] text-[var(--overdue-ink)]' : 'bg-[var(--surface-sunken)] text-[var(--ink-tertiary)]'}`}>{count}</span>
      <div className="h-px flex-1 bg-[var(--border)]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-24">
      <MobileHeader title="Inspections" showBack />

      <div className="px-4 py-4">
        {/* tabs */}
        <div className="flex gap-1 p-1 rounded-[12px] bg-[var(--surface-sunken)] border border-[var(--border)]" style={{ height: 44 }}>
          {[{ k: 'due', l: 'Due today', c: due.length }, { k: 'history', l: 'My history' }].map((t) => {
            const sel = tab === t.k;
            return (
              <button key={t.k} onClick={() => setTab(t.k)} data-testid={`insp-tab-${t.k}`}
                className={`flex-1 rounded-[9px] flex items-center justify-center gap-1.5 text-[13px] transition-colors ${sel ? 'bg-white font-bold text-[var(--ink-primary)]' : 'text-[var(--ink-secondary)] font-medium'}`}
                style={sel ? { border: '1px solid #C4BFB3' } : {}}>
                {t.l}{t.c != null && <span className="font-mono text-[11px] text-[var(--ink-tertiary)]">{t.c}</span>}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>
        ) : tab === 'due' ? (
          <div className="mt-4">
            {due.length === 0 ? (
              <MobileCard className="text-center py-8">
                <CheckCircle2 className="w-10 h-10 text-[var(--pass-ink)] mx-auto mb-2" />
                <p className="text-[var(--ink-tertiary)]">All inspections complete</p>
              </MobileCard>
            ) : (
              <>
                {overdue.length > 0 && (<><SectionHeader label="Overdue" count={overdue.length} tone="overdue" />{overdue.map((it, i) => <Row key={i} item={it} />)}</>)}
                {dueToday.length > 0 && (<><SectionHeader label="Due today" count={dueToday.length} tone="neutral" />{dueToday.map((it, i) => <Row key={i} item={it} />)}</>)}
                <button onClick={() => navigate('/mobile/scan?action=inspect')} data-testid="insp-scan-btn"
                  className="w-full flex items-center justify-center gap-2 mt-3 bg-white rounded-[13px]" style={{ height: 52, border: '1.5px solid #1A1A1A' }}>
                  <QrCode className="w-[18px] h-[18px] text-[var(--ink-primary)]" />
                  <span className="text-[14px] font-semibold text-[var(--ink-primary)]">Scan another asset</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="mt-4">
            {history.length === 0 ? (
              <MobileCard className="text-center py-8"><ClipboardCheck className="w-10 h-10 text-[var(--ink-tertiary)] mx-auto mb-2" /><p className="text-[var(--ink-tertiary)]">No inspection history</p></MobileCard>
            ) : history.map((ins) => {
              const passed = ins.status === 'passed';
              return (
                <MobileCard key={ins.id} spine={passed ? 'var(--pass)' : 'var(--overdue)'} className="mb-2.5" padding="normal">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {passed ? <CheckCircle2 className="w-4 h-4 text-[var(--pass-ink)] shrink-0" /> : <XCircle className="w-4 h-4 text-[var(--critical-ink)] shrink-0" />}
                        <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)] truncate">{ins.asset_unit_number || ins.asset_name}</span>
                      </div>
                      <div className="text-[12.5px] text-[var(--ink-secondary)] truncate mt-0.5">{ins.template_name || 'Inspection'}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-[12px] font-semibold ${passed ? 'text-[var(--pass-ink)]' : 'text-[var(--critical-ink)]'}`}>{(ins.items_passed || 0)} passed · {(ins.items_failed || 0)} failed</div>
                      <div className="font-mono text-[10.5px] text-[var(--ink-tertiary)] mt-0.5">{formatDate(ins.created_at)}</div>
                    </div>
                  </div>
                </MobileCard>
              );
            })}
            {!loading && history.length > 0 && (
              <>
                <div ref={histSentinel} />
                {loadingMore && <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" /></div>}
                {!histHasMore && <p className="text-center text-[12px] text-[var(--ink-tertiary)] py-3">{histTotal} inspection{histTotal === 1 ? '' : 's'}</p>}
              </>
            )}
          </div>
        )}
      </div>

      <MobileNav />
    </div>
  );
};

export default MobileInspections;
